import { Banner } from "@freightsimple/ui-core/Display/Banner";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { CardBrandIcon } from "@freightsimple/ui-core/Display/CardBrandIcon";
import { Link } from "@freightsimple/ui-core/Typography/Link";
import { Table } from "@freightsimple/ui-core/Table/Table";
import type { Column } from "@freightsimple/ui-core/Table/Table";
import RedExclamationAnimatedIcon from "@/Shared/Animations/RedExclamationAnimatedIcon";
import { formatPrice } from "@/Helpers/formatPrice";
import type { Invoice } from "@freightsimple/generated-dashboard-openapi-client/models/Invoice";
import { Currency } from "@freightsimple/generated-dashboard-openapi-client/models/Currency";
import { InvoiceStanding } from "@freightsimple/generated-dashboard-openapi-client/models/InvoiceStanding";
import { InvoiceState } from "@freightsimple/generated-dashboard-openapi-client/models/InvoiceState";
import { InvoiceType } from "@freightsimple/generated-dashboard-openapi-client/models/InvoiceType";
import { PaymentMethod } from "@freightsimple/generated-dashboard-openapi-client/models/PaymentMethod";
import moment from "moment";
import type { ReactNode } from "react";
import ShipmentSection from "@/Shared/Shipment/CustomComponents/ShipmentSection";
import { invoiceTypeDescription } from "@/Shared/Invoices/Helpers/invoiceTypeDescription";
import { invoicesTotalSpend } from "@/Features/Invoices/Exports/Helpers/invoicesTotalSpend";
import { formatSignedPrice } from "@/Helpers/formatSignedPrice";
import QuotesBeforeCouponTag from "@/Shared/Quotes/CustomComponents/QuotesBeforeCouponTag";

// Voided invoices grey out their whole row.
function chargeTextClass(invoice: Invoice, defaultClass: string) {
  return invoice.invoiceState === InvoiceState.Voided
    ? "text-content-faint"
    : defaultClass;
}

// The amount of a voided invoice is struck through on top of being greyed, so
// it reads as a figure left out of the TOTAL rather than one to add up.
function amountTextClass(invoice: Invoice, defaultClass: string) {
  return invoice.invoiceState === InvoiceState.Voided
    ? `${chargeTextClass(invoice, defaultClass)} line-through`
    : defaultClass;
}

interface BillingSectionProps {
  children?: ReactNode | undefined;
  invoices: Array<Invoice>;
  onDownloadInvoice: (invoiceIdentifier: string, key: string) => void;
  onDownloadAllInvoices: () => void;
  downloadingAll?: boolean | undefined;
  downloadingInvoiceId?: string | undefined;
  totalCouponAmount: number;
}

function pricePrefix(invoiceType: InvoiceType): string {
  switch (invoiceType) {
    case InvoiceType.ShipmentCharge:
      return "";
    case InvoiceType.AdditionalCharge:
      return "";
    case InvoiceType.ShipmentRefund:
      return "-";
    case InvoiceType.AdditionalChargeRefund:
      return "-";
  }
}

function describePaymentMethod(paymentMethod: PaymentMethod | undefined) {
  if (paymentMethod === undefined) {
    return "";
  }

  switch (paymentMethod) {
    case PaymentMethod.BankAccount:
      return "Direct Debit";
    case PaymentMethod.Cash:
      return "Cash";
    case PaymentMethod.Cheque:
      return "Cheque";
    case PaymentMethod.CreditCard:
      return "Credit Card";
    case PaymentMethod.Eft:
      return "EFT/ACH";
    case PaymentMethod.InteracETransfer:
      return "Interac eTransfer";
    case PaymentMethod.Wire:
      return "Wire";
  }
}

function currencyToString(currency: Currency): string {
  return currency === Currency.Cad ? "CAD" : "USD";
}

// Small status pill for issued invoices (Unpaid gold, Overdue/Failed red).
function unpaidPillClasses(isOverdueOrFailed: boolean) {
  return `rounded-[10px] px-[10px] py-[2px] text-base font-normal ${
    isOverdueOrFailed ? "bg-red text-white" : "bg-gold text-on-bright"
  }`;
}

const cardDescriptionClasses = "text-base font-light text-content-muted";
const settlementStatusClasses = "text-xs font-light text-content-muted";

interface PaymentDetailsProps {
  invoice: Invoice;
  isOverdue: boolean;
}

function PaymentDetails_(props: PaymentDetailsProps) {
  const invoice = props.invoice;

  if (invoice.invoiceState === InvoiceState.Unpaid) {
    return (
      <div className="flex flex-row items-center gap-1">
        {invoice.disputed && (
          <div className="rounded-[10px] bg-light-red px-[10px] py-[2px] text-base font-normal text-on-bright">
            Disputed
          </div>
        )}
        <div
          className={unpaidPillClasses(
            props.isOverdue || invoice.lastPaymentAttemptFailed,
          )}
        >
          {invoice.lastPaymentAttemptFailed
            ? "Failed"
            : props.isOverdue
              ? "Overdue"
              : "Open"}
        </div>
      </div>
    );
  }

  if (invoice.invoiceState === InvoiceState.Voided) {
    return <div className="text-base text-content-faint">Voided</div>;
  }

  if (
    invoice.paymentMethod === PaymentMethod.CreditCard &&
    invoice.brand !== undefined
  ) {
    return (
      <div className="flex flex-col items-start">
        <div className="flex flex-row items-center gap-1">
          <CardBrandIcon size="sm" brand={invoice.brand} />
          <div className={cardDescriptionClasses}>
            Card Ending ... {invoice.lastFourDigits}
          </div>
        </div>
        {invoice.invoiceState === InvoiceState.PaymentSent && (
          <div className={settlementStatusClasses}>
            Waiting for Confirmation
          </div>
        )}
      </div>
    );
  } else if (
    invoice.paymentMethod === PaymentMethod.BankAccount &&
    invoice.brand !== undefined
  ) {
    return (
      <div className="flex flex-col items-start">
        <div className={cardDescriptionClasses}>{invoice.brand}</div>
        {invoice.invoiceState === InvoiceState.PaymentSent && (
          <div className={settlementStatusClasses}>
            Waiting for Confirmation
          </div>
        )}
      </div>
    );
  } else {
    return (
      <div className="flex flex-col items-start">
        <div className={cardDescriptionClasses}>
          {describePaymentMethod(invoice.paymentMethod)}
        </div>
        {invoice.invoiceState === InvoiceState.PaymentSent && (
          <div className={settlementStatusClasses}>
            Waiting for Confirmation
          </div>
        )}
      </div>
    );
  }
}

function ViewShipmentsBillingSection(props: BillingSectionProps) {
  const { invoices, totalCouponAmount } = props;
  const couponApplied = totalCouponAmount > 0;

  // Voiding an invoice only changes its state — the failed-payment columns it was
  // carrying survive. Without the voided check, cancelling a declined invoice
  // leaves the customer told to retry a payment for an invoice that no longer
  // exists, and the row it points at shows "Voided" with no Failed pill.
  function anyFailedPayments(): boolean {
    return invoices.some(
      (invoice) =>
        invoice.lastPaymentAttemptFailed &&
        invoice.invoiceState !== InvoiceState.Voided,
    );
  }

  const totalAmount = invoicesTotalSpend(invoices);

  const firstInvoice = invoices[0];
  const showTotal = invoices.length > 1 && firstInvoice !== undefined;

  const columns: Column<Invoice>[] = [
    {
      key: "charge",
      header: "Charge",
      render: (invoice) => (
        <div
          className={`text-[18px] font-medium ${chargeTextClass(invoice, "text-content-secondary")}`}
        >
          {invoiceTypeDescription(invoice.invoiceType)}
        </div>
      ),
    },
    {
      key: "date",
      header: "Date",
      render: (invoice) => (
        <div
          className={`text-base font-light ${chargeTextClass(invoice, "text-content-muted")}`}
        >
          {invoice.invoiceState === InvoiceState.Unpaid ? (
            <div className="flex flex-row items-center gap-1">
              <span>Due</span>
              <span>{moment(invoice.dueDate).format("Do MMMM YYYY")}</span>
              {invoice.standing === InvoiceStanding.Overdue && (
                <RedExclamationAnimatedIcon />
              )}
            </div>
          ) : (
            moment(invoice.invoiceDate).format("Do MMMM YYYY")
          )}
        </div>
      ),
    },
    {
      key: "payment",
      header: "Payment",
      render: (invoice) => (
        <PaymentDetails_
          invoice={invoice}
          isOverdue={invoice.standing === InvoiceStanding.Overdue}
        />
      ),
    },
    {
      key: "amount",
      header: "Amount",
      align: "right",
      render: (invoice) => (
        <div className="flex flex-col items-end">
          {couponApplied &&
          invoice.invoiceType === InvoiceType.ShipmentCharge ? (
            <QuotesBeforeCouponTag
              priceBeforeCoupon={invoice.amount + totalCouponAmount}
              size="small"
            />
          ) : undefined}
          <div className="flex flex-row items-center gap-1 whitespace-nowrap">
            <span
              className={`text-[18px] font-medium ${amountTextClass(invoice, "text-content-secondary")}`}
            >
              ${pricePrefix(invoice.invoiceType)}
              {invoice.amount.toFixed(2)}
            </span>
            <span
              className={`text-[18px] font-medium ${amountTextClass(invoice, "text-content-muted")}`}
            >
              {currencyToString(invoice.currency)}
            </span>
          </div>
        </div>
      ),
    },
  ];

  return (
    <ShipmentSection
      title="Billing"
      subtitle={
        <div className="flex flex-col items-start">
          <div>
            Here are all the invoices attached to this shipment. To find other
            invoices, or summaries please go to the{" "}
            <Link href="/view-invoices">billing section</Link>
          </div>

          {anyFailedPayments() && (
            <>
              <div className="h-4" />
              <div>
                There are failed payment attempts below. Please update your{" "}
                <Link href="/credit-cards">payment methods</Link> and{" "}
                <Link href="/view-invoices">retry the payment</Link>, or contact
                us to resolve.
              </div>
            </>
          )}
        </div>
      }
      id="billing-section"
    >
      {couponApplied ? (
        <>
          <Banner variant="info">
            {`A $${formatPrice(totalCouponAmount)} ${currencyToString(invoices[0]?.currency || Currency.Cad)} coupon was applied to this shipment`}
          </Banner>
          <div className="h-4" />
        </>
      ) : undefined}

      <Table
        columns={columns}
        data={invoices}
        rowKey={(invoice) => invoice.invoiceId}
        rowActions={(invoice) => (
          <Button
            onClick={function () {
              props.onDownloadInvoice(
                invoice.invoiceIdentifier,
                invoice.invoicePdfKey,
              );
            }}
            loading={props.downloadingInvoiceId === invoice.invoiceId}
          >
            Download Invoice
          </Button>
        )}
        {...(showTotal && {
          footerLeft: (
            <span className="text-[18px] font-medium text-content-secondary">
              TOTAL
            </span>
          ),
          footerRight: (
            <div className="flex flex-row items-center gap-4">
              <div className="flex flex-row items-center gap-1 whitespace-nowrap">
                <span className="text-[18px] font-medium text-content-secondary">
                  {formatSignedPrice(totalAmount)}
                </span>
                <span className="text-[18px] font-medium text-content-muted">
                  {currencyToString(firstInvoice.currency)}
                </span>
              </div>
              <Button
                onClick={props.onDownloadAllInvoices}
                loading={props.downloadingAll}
              >
                Download All
              </Button>
            </div>
          ),
        })}
      />
    </ShipmentSection>
  );
}
export default ViewShipmentsBillingSection;
